// Web Push subscription manager for AyurClinic
import api from './api';

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export function isPushSupported() {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

export function getPushPermission() {
  if (!isPushSupported()) return 'unsupported';
  return Notification.permission;
}

export async function registerServiceWorker() {
  if (!isPushSupported()) return null;
  try {
    const registration = await navigator.serviceWorker.register('/sw.js', {
      scope: '/',
    });
    return registration;
  } catch (err) {
    console.warn('[Push] Service worker registration failed:', err);
    return null;
  }
}

export async function subscribeUserToPush() {
  if (!isPushSupported()) {
    return { success: false, reason: 'Push notifications are not supported in this browser.' };
  }

  try {
    const perm = await Notification.requestPermission();
    if (perm !== 'granted') {
      return { success: false, reason: 'Notification permission was denied.' };
    }

    const reg = await registerServiceWorker();
    if (!reg) {
      return { success: false, reason: 'Service worker registration failed.' };
    }

    // Await active SW
    await navigator.serviceWorker.ready;

    // Fetch VAPID key
    const keyResp = await api.get('/api/push/vapid-public-key');
    const vapidKey = keyResp.data?.public_key;
    if (!vapidKey) {
      return { success: false, reason: 'VAPID public key not configured on server.' };
    }

    const convertedVapidKey = urlBase64ToUint8Array(vapidKey);

    let subscription = await reg.pushManager.getSubscription();
    if (!subscription) {
      subscription = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: convertedVapidKey,
      });
    }

    const rawSub = subscription.toJSON();
    const payload = {
      endpoint: rawSub.endpoint,
      p256dh: rawSub.keys?.p256dh || '',
      auth: rawSub.keys?.auth || '',
      user_agent: navigator.userAgent,
    };

    await api.post('/api/push/subscribe', payload);
    return { success: true, subscription };
  } catch (err) {
    console.warn('[Push] Failed to subscribe to Web Push:', err);
    return { success: false, reason: err.message || 'Subscription failed.' };
  }
}

export async function unsubscribeUserFromPush() {
  if (!isPushSupported()) return { success: false };

  try {
    const reg = await navigator.serviceWorker.ready;
    const subscription = await reg.pushManager.getSubscription();
    if (subscription) {
      const endpoint = subscription.endpoint;
      await subscription.unsubscribe();
      try {
        await api.delete('/api/push/unsubscribe', { data: { endpoint } });
      } catch (e) {
        console.warn('[Push] Failed to notify backend of unsubscription', e);
      }
    }
    return { success: true };
  } catch (err) {
    console.warn('[Push] Failed to unsubscribe:', err);
    return { success: false, error: err };
  }
}
